import { useCallback } from "react";

import { FormOutlined } from "@ant-design/icons";
import { Tooltip, Typography } from "antd";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { RVStatusDisplay, RVLoadingTitle, RVStatusBadge, BADGE_STATUS, RVButton } from "@regal-voice/ui";
import { useFlags } from "Services/FeatureFlagService";
import {
    selectState,
    selectFriendlyId,
    selectVersion,
    setEditingJourneyDetails,
    selectDetails,
} from "Services/state/journeys/JourneyStateSlice";
import { useRVDispatch } from "Services/state/Storage";
import { ToggleCollapsiblePanelButton, useKnownPanels } from "Utils/CollapsiblePanels";

import { getJourneyState, isNewJourney } from "../../helpers";
import { TagCount } from "./TagCount";

import styles from "./TitleBar.module.scss";

function JourneyName({ loading }: { loading: boolean }): JSX.Element {
    const dispatch = useRVDispatch();
    const { name } = useSelector(selectDetails);

    const onClick = useCallback(() => {
        dispatch(setEditingJourneyDetails(true));
    }, [dispatch]);

    return loading ? (
        <RVLoadingTitle />
    ) : (
        <div onClick={onClick} className={styles.titleNameContainer}>
            <Typography.Title level={1}> {name} </Typography.Title>
            <span className={styles.titleBarCTA}>
                <TagCount />
                <Tooltip title="Edit Journey Name and Details">
                    <FormOutlined className={styles.previewIcon} />
                </Tooltip>
            </span>
        </div>
    );
}

type TitleBarProps = {
    journeyId: string;
    loading: boolean;
    onVersionClick?: () => void;
    showVersionsPanel?: boolean;
    onSearchExecutions?: () => void;
};

export function TitleBar({
    journeyId,
    loading,
    onVersionClick,
    showVersionsPanel = false,
    onSearchExecutions,
}: TitleBarProps): JSX.Element {
    const state = useSelector(selectState);
    const friendlyId = useSelector(selectFriendlyId);
    const version = useSelector(selectVersion);
    const { journeyExecutionMonitor } = useFlags();
    const { orchestration: journeysPanel } = useKnownPanels();

    const { status, label } = getJourneyState(state);
    const statusId = journeyId != "new" ? friendlyId : "";

    return (
        <div className={clsx("flex-row justify-between align-center", styles.titleContainer)}>
            <div className={clsx("flex-row align-center", styles.collapseContainer)}>
                <ToggleCollapsiblePanelButton panelName={journeysPanel.name} direction="left" />
                <JourneyName loading={loading} />
            </div>

            <div className={clsx(styles.statusDisplayContainer, { [styles.withVersionPanel]: showVersionsPanel })}>
                {journeyExecutionMonitor && !isNewJourney(journeyId) && onSearchExecutions && (
                    <RVButton type="link" className={styles.searchExecutionsLink} onClick={onSearchExecutions}>
                        Search Executions
                    </RVButton>
                )}
                <RVStatusDisplay
                    id={statusId}
                    status={showVersionsPanel ? undefined : status}
                    content={showVersionsPanel ? undefined : label}
                    loading={loading}
                    version={String(version)}
                    isVersionClickable={!isNewJourney(journeyId)}
                    onVersionClick={onVersionClick}
                    versionTooltipText="Journey Version History"
                />
                {showVersionsPanel && <RVStatusBadge status={BADGE_STATUS.VIEW_ONLY} content="View Only" />}
            </div>
        </div>
    );
}
